import type { ProjectState, Proposal } from "@minga/core"
import { DECISIONS_MAX } from "@minga/core"
import type { DecidedHeader } from "@minga/protocol"
import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { useOptionalProject } from "@/features/project/project-context"

/**
 * K0 (wave K §10.1, §10.2): decided cards from the Project DO's `decided` projection, which keeps them after snapshot
 * compaction drops them from the state (7 days after they were resolved). The reads are keyed on the newest
 * `resolution.at` in the state, so a new decision refetches them; while the server is still backfilling the projection
 * (`building`, a project with no commit since the deploy) they refetch every few seconds until it is done.
 */

/** How often a decided read refetches while the server answers `building`. */
export const DECIDED_BUILDING_REFETCH_MS = 4_000

const whileBuilding = (q: {
  readonly state: { readonly data?: { readonly building: boolean } | undefined }
}) => (q.state.data?.building ? DECIDED_BUILDING_REFETCH_MS : false)

/** The newest `resolution.at` among the state's proposals (0 when none is resolved): the decided reads' key. */
export const newestResolutionAt = (s: Pick<ProjectState, "proposals">): number => {
  let at = 0
  for (const p of s.proposals) if (p.resolution && p.resolution.at > at) at = p.resolution.at
  return at
}

const NO_HEADERS: ReadonlyArray<DecidedHeader> = []
const NO_CARDS: ReadonlyArray<Proposal> = []
const NO_INDEX = { headers: NO_HEADERS, building: false } as const
const NO_CARDS_READ = { cards: NO_CARDS, building: false } as const

/** `DecidedIndex`: every decided card's header, newest first (`P-n` chips, ⌘K). Empty while loading or offline. */
export const useDecidedIndex = (enabled = true): ReadonlyArray<DecidedHeader> => {
  const ctx = useOptionalProject()
  const knowledge = ctx?.knowledge
  const key = ctx ? newestResolutionAt(ctx.state) : 0
  const query = useQuery({
    queryKey: ["decided-index", ctx?.projectId ?? "", key],
    queryFn: () => (knowledge ? knowledge.decidedIndex() : Promise.resolve(NO_INDEX)),
    enabled: enabled && knowledge !== undefined,
    staleTime: Number.POSITIVE_INFINITY,
    refetchInterval: whileBuilding,
    placeholderData: keepPreviousData,
    retry: 1,
  })
  return query.data?.headers ?? NO_HEADERS
}

/** `DecidedCards{limit: DECISIONS_MAX}`: the newest decided cards, for the Overview's Decisions. */
export const useDecidedCards = (limit: number = DECISIONS_MAX): ReadonlyArray<Proposal> => {
  const ctx = useOptionalProject()
  const knowledge = ctx?.knowledge
  const key = ctx ? newestResolutionAt(ctx.state) : 0
  const query = useQuery({
    queryKey: ["decided-cards", ctx?.projectId ?? "", key, limit],
    queryFn: () => (knowledge ? knowledge.decidedCards({ limit }) : Promise.resolve(NO_CARDS_READ)),
    enabled: knowledge !== undefined,
    staleTime: Number.POSITIVE_INFINITY,
    refetchInterval: whileBuilding,
    placeholderData: keepPreviousData,
    retry: 1,
  })
  return query.data?.cards ?? NO_CARDS
}

/** One decided card: from the state when it is there, else `DecidedCards{ids: [id]}`. */
export const useDecidedCard = (
  proposalId: string | null,
): { readonly card: Proposal | null; readonly loading: boolean; readonly error: boolean } => {
  const ctx = useOptionalProject()
  const knowledge = ctx?.knowledge
  const inState = proposalId ? (ctx?.state.proposals.find((p) => p.id === proposalId) ?? null) : null
  const query = useQuery({
    queryKey: ["decided-card", ctx?.projectId ?? "", proposalId ?? ""],
    queryFn: async () =>
      knowledge && proposalId ? await knowledge.decidedCards({ ids: [proposalId] }) : NO_CARDS_READ,
    enabled: proposalId !== null && inState === null && knowledge !== undefined,
    staleTime: Number.POSITIVE_INFINITY,
    refetchInterval: whileBuilding,
    retry: 1,
  })
  if (inState) return { card: inState, loading: false, error: false }
  return {
    card: query.data?.cards[0] ?? null,
    loading: proposalId !== null && query.isPending && knowledge !== undefined,
    error: query.isError,
  }
}
